import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Paper, CircularProgress, Alert,
  Button, Chip, Divider, IconButton, Stack, Tooltip, Drawer,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { useCart } from '../../contexts/CartContext';
import { formatCurrency } from '../../utils/currencyFormatter';
import { StockLevel, CategoryPill } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';
import AddToCartControl from './AddToCartControl';

const ProductDetail = () => {
  const { productId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { addToCart, cart } = useCart();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [addedDrawerOpen, setAddedDrawerOpen] = useState(false);
  const [lastAddedQty, setLastAddedQty] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setError('');
      const companyId = localStorage.getItem('companyId');
      try {
        const productRequest = axios.get(`${config.backendUrl}/products/${productId}`);
        const categoriesRequest = companyId
          ? axios.get(`${config.backendUrl}/categories?companyId=${companyId}`)
          : Promise.resolve({ data: [] });
        const inventoryRequest = companyId
          ? axios.get(`${config.backendUrl}/inventory?companyId=${companyId}`)
          : Promise.resolve({ data: [] });

        const [productRes, categoriesRes, inventoryRes] = await Promise.all([productRequest, categoriesRequest, inventoryRequest]);
        setProduct(productRes.data);
        setCategories(categoriesRes.data || []);

        const inventoryItem = inventoryRes.data?.find((item) => item.productId === productId);
        setInventory(inventoryItem || null);

        try {
          if (productRes.data?.productName) {
            sessionStorage.setItem(`breadcrumb:${productId}`, productRes.data.productName);
            window.dispatchEvent(new CustomEvent('breadcrumb-label-update', {
              detail: { id: productId, label: productRes.data.productName }
            }));
          }
        } catch (e) {
          console.warn('Unable to cache breadcrumb label', e);
        }
      } catch (err) {
        setError(t('Failed to load product', { defaultValue: 'Failed to load product' }));
      } finally {
        setLoading(false);
      }
    };

    if (config?.backendUrl) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, productId, t]);

  const mediaItems = useMemo(() => {
    if (!product) return [];
    const imgs = product.productImages && product.productImages.length > 0
      ? product.productImages
      : (product.productImage ? [product.productImage] : []);
    const items = imgs.map((url) => ({ type: 'image', url }));
    if (product.productVideo) {
      items.push({ type: 'video', url: product.productVideo });
    }
    return items;
  }, [product]);

  const category = useMemo(() => {
    if (!product?.categoryId || !categories?.length) return null;
    return categories.find((c) => c._id === product.categoryId) || null;
  }, [categories, product]);

  const currencyCode = useMemo(() => product?.currencyCode || localStorage.getItem('currencyCode') || 'USD', [product]);
  const displayPrice = useMemo(
    () => formatCurrency(Number(product?.productPrice || 0), currencyCode, i18n.language),
    [currencyCode, i18n.language, product]
  );

  const sku = product?.sku || product?.productSku || product?._id;
  const isYouTube = (url) => /youtu\.?be/.test(url || '');

  const renderMainMedia = (item) => {
    if (!item) {
      return (
        <Box
          sx={{
            width: '100%', minHeight: 320, bgcolor: 'background.default', display: 'flex',
            alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'divider',
            borderRadius: `${radius.lg}px`,
          }}
        >
          <Typography color="text.secondary">{t('No Image')}</Typography>
        </Box>
      );
    }
    if (item.type === 'video') {
      return isYouTube(item.url) ? (
        <Box sx={{ position: 'relative', pt: '56.25%', borderRadius: `${radius.lg}px`, overflow: 'hidden' }}>
          <iframe
            src={item.url.replace('watch?v=', 'embed/')}
            title="product video"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            allowFullScreen
          />
        </Box>
      ) : (
        <Box component="video" controls sx={{ width: '100%', bgcolor: 'common.black', borderRadius: `${radius.lg}px` }}>
          <source src={item.url} />
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={item.url}
        alt={product?.productName}
        sx={{ width: '100%', maxHeight: 420, objectFit: 'contain', bgcolor: 'background.paper', borderRadius: `${radius.lg}px` }}
      />
    );
  };

  const handleAdd = async (qty) => {
    const result = await addToCart(product._id, qty);
    if (result?.success !== false) {
      setLastAddedQty(qty);
      setAddedDrawerOpen(true);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error || !product) {
    return <Alert severity="error" sx={{ mt: 4 }}>{error || t('Product not found', { defaultValue: 'Product not found' })}</Alert>;
  }

  const current = mediaItems[activeIndex];
  const displayName = product.productName || product.name || sku || t('Product');
  const alreadyInCart = cart?.items?.find((item) => item.productId === product._id)?.quantity || 0;

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Box onClick={() => navigate('/shop')} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, cursor: 'pointer', color: 'primary.main' }}>
          <NavigateBeforeIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>{t('Browse products', { defaultValue: 'Browse products' })}</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
              {renderMainMedia(current)}
              {mediaItems.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ mt: 2, overflowX: 'auto' }}>
                  {mediaItems.map((m, idx) => (
                    <Tooltip key={`${m.type}-${idx}`} title={m.type === 'video' ? t('Preview') : product.productName}>
                      <Box
                        onClick={() => setActiveIndex(idx)}
                        sx={{
                          width: 66, height: 66, borderRadius: `${radius.md}px`,
                          border: idx === activeIndex ? '2px solid' : '1px solid',
                          borderColor: idx === activeIndex ? 'primary.main' : 'divider',
                          overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default',
                        }}
                      >
                        {m.type === 'video' ? (
                          <PlayCircleFilledWhiteIcon color={idx === activeIndex ? 'primary' : 'action'} />
                        ) : (
                          <Box component="img" src={m.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                </Stack>
              )}
              {mediaItems.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <IconButton onClick={() => setActiveIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1))}>
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton onClick={() => setActiveIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1))}>
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
              {category && <CategoryPill color={category.categoryColor} label={category.categoryName} />}
              <Chip label={`${t('per')} ${product.unit || t('N/A')}`} size="small" />
            </Stack>
            <Typography variant="h4" sx={{ mb: 1.5 }}>{displayName}</Typography>
            <Typography variant="h5" color="primary" fontWeight={700} sx={{ mb: 2 }}>{displayPrice}</Typography>

            <StockLevel
              quantity={inventory?.quantity || 0}
              threshold={inventory?.lowStockThreshold ?? 10}
              unit={t('{{unit}} available', { unit: product.unit || '', defaultValue: `${product.unit || ''} available` })}
              labels={{ inStock: t('In stock'), lowStock: t('Low stock'), outOfStock: t('Out of stock') }}
              sx={{ mb: 2.5, maxWidth: 340 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
              {product.productDescription || t('Description')}
            </Typography>

            <Divider sx={{ mb: 2.5 }} />

            <AddToCartControl
              available={inventory?.quantity || 0}
              alreadyInCart={alreadyInCart}
              onAdd={handleAdd}
              unit={product.unit}
            />

            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: `${radius.md}px`, bgcolor: 'action.hover' }}>
              <LocalShippingOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} />
              <Typography variant="body2" color="text.secondary">
                {t('Usually delivered in 2–3 business days', { defaultValue: 'Usually delivered in 2–3 business days' })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Drawer anchor="right" open={addedDrawerOpen} onClose={() => setAddedDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100%', sm: 360 }, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">{t('Added to cart', { defaultValue: 'Added to cart' })}</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{product.productName}</Typography>
              <Typography variant="caption" color="text.secondary">{t('Qty')}: {lastAddedQty}</Typography>
            </Box>
            <Typography variant="body2" fontWeight={700}>{formatCurrency((product.productPrice || 0) * lastAddedQty, currencyCode, i18n.language)}</Typography>
          </Paper>
          <Stack spacing={1.25}>
            <Button variant="contained" fullWidth onClick={() => navigate('/cart')}>{t('View Cart')}</Button>
            <Button variant="outlined" fullWidth onClick={() => setAddedDrawerOpen(false)}>{t('Continue Shopping')}</Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default ProductDetail;
